/**
 * `MessageList` — the scrolling transcript surface for the Secret Chat
 * conversation view.
 *
 * Responsibilities (requirements coverage R2.4, R4.3, R5.3, R6.1, R6.3,
 * R9.5):
 *   - Render `messages` in ascending seq order (古→新), the natural top-to-
 *     bottom reading direction for a chat log (R5.3, R5.4 via the upstream
 *     `useMessages` invariant).
 *   - Surface sender attribution, body text, and a human-readable timestamp
 *     for every message (R2.4, R4.3). Own and other-party messages are
 *     marked distinctly so the stylesheet can right-/left-align them and
 *     assistive tech can announce authorship cleanly.
 *   - Render a read indicator on own messages: "既読" when
 *     `message.seq <= otherReadReceipt`, "未読" otherwise (R6.1, R6.3).
 *     Other-party messages get no such indicator — the viewer is the only
 *     one whose read state matters for their own screen, and showing a
 *     redundant "you read this" badge would be noise.
 *   - When the user scrolls to (or near) the top of the scroller and more
 *     history exists, invoke `onLoadOlder()` (R5.3). Guards against
 *     re-entry while `isLoadingOlder` is true and against firing when
 *     `hasMore=false`.
 *   - Keep the viewport "stuck" to the bottom on new-message arrival if the
 *     user was already near the bottom; otherwise leave the scroll position
 *     where it was (so loading older history doesn't yank the user away
 *     from the message they're reading).
 *   - Plug into `useReadReceipt.observe` when the optional `observeForRead`
 *     prop is provided. Each rendered message element is registered with
 *     its seq on mount/update, and the returned cleanup runs on unmount or
 *     when the message leaves the rendered set.
 *   - WCAG-AA color contrast (R9.5) is enforced by the stylesheet, not by
 *     this component. Inline styles are avoided so the theme stays in one
 *     place (`web/src/index.css`).
 *
 * Dependency direction (design.md §File Structure Plan):
 *   This component depends only on the `Message` type from `../api/chatApi`
 *   and on `react`. It never calls `chatApi`, `signalRClient`, or any hook
 *   directly — the parent (`ChatView` in task 6.5) wires `useMessages` /
 *   `useReadReceipt` and feeds their outputs in as props. This keeps the
 *   component trivially testable and reusable for fixtures.
 */

import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import type { JSX, UIEvent } from 'react';

import type { ChatApiError, DisplayMessage } from '../api/chatApi';
import { linkify } from '../lib/linkify';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** Function signature mirrors `useReadReceipt.observe`. */
export type ObserveForRead = (el: HTMLElement, seq: number) => () => void;

export interface MessageListProps {
  /**
   * Messages in ascending order (古→新). May include optimistic (`pending`)
   * entries after the newest confirmed message; those render as "送信中" /
   * "失敗" bubbles instead of carrying a read indicator.
   */
  readonly messages: readonly DisplayMessage[];
  /** The viewer's nickname — used to classify own vs other messages. */
  readonly myNickname: string;
  /**
   * Retry handler for a failed optimistic message, keyed by its temp id.
   * Wired by the parent to `useMessages.resend`. When omitted, failed
   * bubbles render without a retry button (e.g. in isolated fixtures).
   */
  readonly onResend?: (tempId: string) => void;
  /**
   * When true and there are no messages yet, render skeleton placeholders
   * instead of the empty-state text — used during the initial history fetch
   * so room entry doesn't flash an empty transcript.
   */
  readonly isInitialLoading?: boolean;
  /**
   * Other participant's last-read seq. Own messages with `seq <=
   * otherReadReceipt` render as "既読"; the rest render as "未読".
   * Zero means "nothing read yet" (the server's initial state).
   */
  readonly otherReadReceipt: number;
  /**
   * Whether the *other* participant has read receipts enabled. When `false`,
   * own messages render with no read indicator at all — the other side has
   * opted out of letting us see their reads, so a forever-"未読" badge would
   * be misleading. Defaults to `true` for backward compatibility.
   */
  readonly otherShowReadReceipt?: boolean;
  /** Whether more older history exists on the server. */
  readonly hasMore: boolean;
  /** True while an older-page fetch is in flight (suppresses re-entry). */
  readonly isLoadingOlder?: boolean;
  /** Triggered when the user scrolls to the top and more history exists. */
  readonly onLoadOlder: () => void;
  /**
   * Optional read-receipt registrar. When supplied, every rendered message
   * element is observed with its seq; the returned teardown is invoked on
   * unmount or when the element leaves the rendered set.
   */
  readonly observeForRead?: ObserveForRead;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Pixel distance from the top edge at which we consider the user to be
 * "asking for older messages". Picked to allow a small overscroll without
 * triggering on a one-pixel rubber-band.
 */
const LOAD_OLDER_TOP_THRESHOLD_PX = 80;

/**
 * Pixel distance from the bottom edge below which we keep the viewport
 * pinned to the bottom on new-message arrival. Slightly wider than the top
 * threshold because the "tail follow" affordance is more forgiving — users
 * expect the latest message to remain visible even when they've idly
 * scrolled up a tiny bit.
 */
const STICK_TO_BOTTOM_THRESHOLD_PX = 120;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format an ISO 8601 timestamp for inline display. Locale-aware. */
function formatTime(iso: string): string {
  // `Date` will return "Invalid Date" for malformed input. We defer the
  // formatting failure mode to the renderer rather than throwing — a
  // missing timestamp is preferable to a crashed transcript.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // 24-hour HH:mm. Locale "ja-JP" gives "12:34" which is the common chat
  // display; the explicit hour12: false guards against environments that
  // default to a 12-hour clock for ja-JP.
  return d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', hour12: false });
}

const WEEKDAY_JA = ['日', '月', '火', '水', '木', '金', '土'] as const;

/**
 * Local-time day key (YYYY-MM-DD) used to detect day boundaries between
 * adjacent messages. We use local time deliberately — LINE-style separators
 * reflect the date the user perceives in their own timezone, not UTC.
 */
function localDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * LINE-style date label.
 *   - Today    → "今日"
 *   - Yesterday → "昨日"
 *   - Same year → "M月D日（曜）"
 *   - Else      → "YYYY年M月D日（曜）"
 *
 * Returns an empty string for malformed input so the caller can decide to
 * omit the separator entirely rather than render garbage.
 */
function formatDateLabel(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';

  const target = localDateKey(d);
  const today = localDateKey(now);
  const yesterdayDate = new Date(now);
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const yesterday = localDateKey(yesterdayDate);

  const weekday = WEEKDAY_JA[d.getDay()];

  if (target === today) return `今日（${weekday}）`;
  if (target === yesterday) return `昨日（${weekday}）`;

  const sameYear = d.getFullYear() === now.getFullYear();
  if (sameYear) {
    return `${d.getMonth() + 1}月${d.getDate()}日（${weekday}）`;
  }
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日（${weekday}）`;
}

/** Returns true when the scroller is within `threshold` pixels of its bottom. */
function isNearBottom(el: HTMLElement, threshold: number): boolean {
  return el.scrollHeight - (el.scrollTop + el.clientHeight) <= threshold;
}

/**
 * Short Japanese reason for a failed optimistic send, shown next to the
 * retry button on a `failed` bubble. Deliberately concise (the bubble is
 * tight on space); the localized strings mirror those used elsewhere
 * (`MessageInput` / `PasswordEntry`) but are kept local — duplicating a few
 * lines is preferred here over coupling MessageList to those components.
 */
function describeSendError(error: ChatApiError | undefined): string {
  if (error === undefined) return '送信に失敗しました';
  switch (error.kind) {
    case 'rateLimited':
      return `送信が多すぎます（${error.retryAfterSeconds}秒後に再送）`;
    case 'networkError':
      return 'ネットワークエラー';
    case 'serverError':
      return 'サーバエラー';
    case 'concurrencyConflict':
      return '一時的な競合が発生しました';
    case 'notJoined':
      return 'ルームに参加していません';
    case 'roomNotFound':
      return 'ルームが見つかりません';
    case 'guestLimitReached':
      return `連続送信は ${error.limit} 件までです`;
    case 'bannedWord':
      return error.word.length > 0
        ? `NGワード「${error.word}」を含みます`
        : 'NGワードを含みます';
    case 'invalidInput':
      return '入力内容を確認してください';
    case 'roomFull':
      return 'ルームが満員です';
    default:
      return '送信に失敗しました';
  }
}

/**
 * Number of skeleton bubble rows rendered during the initial history fetch.
 * Picked to fill a typical viewport without implying a specific message
 * count.
 */
const INITIAL_SKELETON_ROWS = 6;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MessageList(props: MessageListProps): JSX.Element {
  const {
    messages,
    myNickname,
    otherReadReceipt,
    otherShowReadReceipt = true,
    hasMore,
    isLoadingOlder = false,
    onLoadOlder,
    observeForRead,
    onResend,
    isInitialLoading = false,
  } = props;

  // Stable ref to the outer scroll container. Used for scroll-position
  // observation and programmatic "stick to bottom".
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  // Snapshot of "was the user near the bottom right before this update?"
  // — captured synchronously *before* the DOM mutates so the post-commit
  // effect can decide whether to auto-scroll.
  const wasNearBottomRef = useRef<boolean>(true);

  // Latest props captured by ref so the scroll handler stays referentially
  // stable across renders. Avoids re-attaching listeners on every prop
  // tweak from the parent.
  const hasMoreRef = useRef<boolean>(hasMore);
  hasMoreRef.current = hasMore;
  const isLoadingOlderRef = useRef<boolean>(isLoadingOlder);
  isLoadingOlderRef.current = isLoadingOlder;
  const onLoadOlderRef = useRef<() => void>(onLoadOlder);
  onLoadOlderRef.current = onLoadOlder;

  // Track the previous bottom of the message list so we know when a new
  // message has been appended vs only an older-history prepend happened.
  const prevLastSeqRef = useRef<number>(-1);

  // -------------------------------------------------------------------------
  // Read-receipt registration
  // -------------------------------------------------------------------------
  // We keep one teardown per (DOM element, seq) pair so that re-observing
  // the same element with a new seq cleanly disposes the old registration.
  // The map is keyed by seq because that's what the hook's contract treats
  // as the unique identifier of an observation.
  const observeTeardownsRef = useRef<Map<number, () => void>>(new Map());

  // Stable callback ref factory — passed to each message element's `ref`
  // attribute. We can't use `useCallback` per seq, so each render allocates
  // a small closure. The cost is negligible (one closure per visible
  // message) and avoids leaking observer state through hook-internal
  // identity tricks.
  const registerElement = useCallback(
    (seq: number) => (el: HTMLLIElement | null) => {
      const teardowns = observeTeardownsRef.current;
      const existing = teardowns.get(seq);

      if (el === null) {
        // React calls us with null when the element is unmounting. Dispose
        // any existing observation for this seq.
        if (existing !== undefined) {
          existing();
          teardowns.delete(seq);
        }
        return;
      }

      // If we already have a teardown for this seq, it's a re-mount of the
      // same logical message (rare — React reuses nodes when keys match).
      // Be defensive: dispose the old before registering the new.
      if (existing !== undefined) {
        existing();
        teardowns.delete(seq);
      }

      if (observeForRead !== undefined) {
        const teardown = observeForRead(el, seq);
        teardowns.set(seq, teardown);
      }
    },
    [observeForRead],
  );

  // Final cleanup on unmount: dispose every remaining observation so the
  // upstream IntersectionObserver doesn't retain detached nodes.
  useEffect(() => {
    return () => {
      const teardowns = observeTeardownsRef.current;
      for (const fn of teardowns.values()) {
        fn();
      }
      teardowns.clear();
    };
  }, []);

  // -------------------------------------------------------------------------
  // Scroll position management
  // -------------------------------------------------------------------------
  // Pre-DOM-update: capture whether the user was near the bottom so the
  // post-update effect can re-stick to bottom if they were.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (el === null) return;
    wasNearBottomRef.current = isNearBottom(el, STICK_TO_BOTTOM_THRESHOLD_PX);
  });

  // Post-DOM-update: decide whether to auto-scroll.
  //   - First render with messages: jump to the bottom so the user sees the
  //     latest message immediately (R5.2 expects the freshest content).
  //   - Subsequent renders: only re-stick to bottom when a NEW message was
  //     appended AND the user was already near the bottom. This keeps the
  //     user's reading position stable when older history is prepended.
  useEffect(() => {
    const el = scrollerRef.current;
    if (el === null) return;

    const lastSeq = messages.length === 0 ? -1 : messages[messages.length - 1].seq;
    const previous = prevLastSeqRef.current;
    prevLastSeqRef.current = lastSeq;

    if (previous === -1) {
      // First render with content — go straight to the bottom.
      if (lastSeq !== -1) {
        el.scrollTop = el.scrollHeight;
      }
      return;
    }

    if (lastSeq > previous && wasNearBottomRef.current) {
      // A new message was appended and the user was tailing — stay glued.
      el.scrollTop = el.scrollHeight;
    }
    // Otherwise (older prepend, no change, user scrolled away): leave it.
  }, [messages]);

  // -------------------------------------------------------------------------
  // Top-edge scroll detection → onLoadOlder()
  // -------------------------------------------------------------------------
  const handleScroll = useCallback((e: UIEvent<HTMLDivElement>) => {
    if (!hasMoreRef.current) return;
    if (isLoadingOlderRef.current) return;
    const el = e.currentTarget;
    if (el.scrollTop <= LOAD_OLDER_TOP_THRESHOLD_PX) {
      onLoadOlderRef.current();
    }
  }, []);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  if (messages.length === 0) {
    // Initial history fetch in flight: show skeleton bubbles so room entry
    // doesn't flash "no messages" before the first page resolves.
    if (isInitialLoading) {
      return (
        <div
          ref={scrollerRef}
          className="message-list message-list--loading"
          role="log"
          aria-live="polite"
          aria-busy="true"
          aria-label="メッセージを読み込んでいます"
        >
          <ul className="message-list__items" aria-hidden="true">
            {Array.from({ length: INITIAL_SKELETON_ROWS }).map((_, i) => (
              <li
                key={`skeleton-${i}`}
                className={
                  i % 2 === 0
                    ? 'message message--other message--skeleton'
                    : 'message message--own message--skeleton'
                }
              >
                <div className="message__column">
                  <div className="message__bubble">
                    <span className="message__skeleton-line" />
                    <span className="message__skeleton-line message__skeleton-line--short" />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      );
    }

    return (
      <div
        ref={scrollerRef}
        className="message-list message-list--empty"
        role="log"
        aria-live="polite"
        aria-label="メッセージ一覧"
      >
        <p className="message-list__empty">まだメッセージはありません</p>
      </div>
    );
  }

  return (
    <div
      ref={scrollerRef}
      className="message-list"
      role="log"
      aria-live="polite"
      aria-label="メッセージ一覧"
      onScroll={handleScroll}
    >
      {isLoadingOlder ? (
        <div className="message-list__loading" aria-live="polite">
          以前のメッセージを読み込んでいます…
        </div>
      ) : null}
      <ul className="message-list__items">
        {(() => {
          const nodes: JSX.Element[] = [];
          // Track the previous message's local-date key so we can insert a
          // LINE-style date banner only when the day boundary changes (and
          // always before the very first message).
          let prevDateKey: string | null = null;
          const now = new Date();

          for (const msg of messages) {
            const d = new Date(msg.sentAt);
            const currentDateKey = Number.isNaN(d.getTime()) ? null : localDateKey(d);
            if (currentDateKey !== null && currentDateKey !== prevDateKey) {
              const label = formatDateLabel(msg.sentAt, now);
              if (label !== '') {
                nodes.push(
                  <li
                    key={`sep-${currentDateKey}`}
                    role="separator"
                    aria-label={label}
                    className="message-list__date-separator"
                    data-date={currentDateKey}
                  >
                    <span className="message-list__date-separator-label">{label}</span>
                  </li>,
                );
              }
              prevDateKey = currentDateKey;
            }

            const pendingStatus = msg.pending?.status;
            const isOwn = msg.senderNickname === myNickname;
            // Suppress the indicator entirely when the message is still
            // pending (no server state yet) or when the other participant has
            // disabled their read-receipt visibility (`otherShowReadReceipt`
            // is `false`). Their reads will never propagate, so a perpetual
            // "未読" would falsely signal an unresponsive partner.
            const readState =
              pendingStatus === undefined && isOwn && otherShowReadReceipt
                ? msg.seq <= otherReadReceipt
                  ? 'read'
                  : 'unread'
                : null;
            const classNames = [
              'message',
              isOwn ? 'message--own' : 'message--other',
            ];
            if (pendingStatus === 'sending') classNames.push('message--sending');
            if (pendingStatus === 'failed') classNames.push('message--failed');
            const ownClass = classNames.join(' ');
            const failureText =
              pendingStatus === 'failed'
                ? describeSendError(msg.pending?.error)
                : '';
            // The aria-label folds the salient facts into one string so screen
            // readers announce "送信者: 本文 (時刻/状態) 既読/未読" without
            // having to traverse children.
            const ariaLabel = [
              isOwn ? `自分（${msg.senderNickname}）` : msg.senderNickname,
              msg.content,
              pendingStatus === undefined ? formatTime(msg.sentAt) : '',
              pendingStatus === 'sending' ? '送信中' : failureText,
              readState === 'read' ? '既読' : readState === 'unread' ? '未読' : '',
            ]
              .filter(Boolean)
              .join(' / ');

            nodes.push(
              <li
                key={msg.id}
                className={ownClass}
                data-own={isOwn ? 'true' : 'false'}
                data-seq={msg.seq}
                data-pending={pendingStatus ?? undefined}
                aria-label={ariaLabel}
                // Pending messages have no server seq, so they're excluded
                // from read-receipt observation.
                ref={pendingStatus === undefined ? registerElement(msg.seq) : undefined}
              >
                {/* LINE 風レイアウト:
                 *   - 相手 (other): 吹き出しの上に小さく相手名を出し、その下に
                 *     吹き出しを並べる。flex 列ラッパーで「名前 + 吹き出し」を
                 *     1 単位にまとめ、メタ (時刻) はその右側に来る。
                 *   - 自分 (own): 名前は表示しない。吹き出しのみが column の
                 *     中身になる。 */}
                <div className="message__column">
                  {!isOwn && (
                    <div className="message__sender">{msg.senderNickname}</div>
                  )}
                  <div className="message__bubble">
                    <div className="message__body">{linkify(msg.content)}</div>
                  </div>
                  {pendingStatus === 'failed' && (
                    <div className="message__send-error" role="alert">
                      <span className="message__send-error-text">
                        {failureText}
                      </span>
                      {onResend !== undefined && msg.pending !== undefined && (
                        <button
                          type="button"
                          className="message__resend"
                          onClick={() => onResend(msg.pending!.tempId)}
                        >
                          再送
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {/* Meta column sits outside the bubble on its inside-facing
                 *   edge (LINE-style). Top→bottom: read indicator (own only),
                 *   then time (or send status for pending messages). */}
                <div className="message__meta">
                  {readState !== null ? (
                    <span
                      className={`message__receipt message__receipt--${readState}`}
                      aria-label={readState === 'read' ? '既読' : '未読'}
                    >
                      {readState === 'read' ? '既読' : '未読'}
                    </span>
                  ) : null}
                  {pendingStatus === 'sending' ? (
                    <span className="message__sending-label">送信中…</span>
                  ) : pendingStatus === undefined ? (
                    <time className="message__time" dateTime={msg.sentAt}>
                      {formatTime(msg.sentAt)}
                    </time>
                  ) : null}
                </div>
              </li>,
            );
          }
          return nodes;
        })()}
      </ul>
    </div>
  );
}
